import React, { useEffect, useState } from 'react';
import { useTyping } from '../../hooks/useTyping';

interface BootSequenceProps {
  onComplete: () => void;
}

const BootSequence: React.FC<BootSequenceProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [bootComplete, setBootComplete] = useState(false);

  const bootSteps = [
    "Initializing Mohammad Tabish Portfolio OS v2.0...",
    "[ ✓ ] Loading personal data...",
    "[ ✓ ] Compiling project portfolio...",
    "[ ✓ ] Establishing secure connection...",
    "[ ✓ ] Mounting skill tree...",
    "[ ✓ ] Loading media gallery..."
  ];

  const asciiArt = `
    ████████╗ █████╗ ██████╗ ██╗███████╗██╗  ██╗
    ╚══██╔══╝██╔══██╗██╔══██╗██║██╔════╝██║  ██║
       ██║   ███████║██████╔╝██║███████╗███████║
       ██║   ██╔══██║██╔══██╗██║╚════██║██╔══██║
       ██║   ██║  ██║██████╔╝██║███████║██║  ██║
       ╚═╝   ╚═╝  ╚═╝╚═════╝ ╚═╝╚══════╝╚═╝  ╚═╝
  `;

  const welcomeMessage = `
Welcome to Mohammad Tabish Mukaddam's Terminal Portfolio
Operations & Technology Professional | Game Developer | AI Enthusiast

Type 'help' to begin your journey.
  `;

  const { displayedText: stepText, isTyping: stepTyping } = useTyping(
    bootSteps[currentStep] || '',
    20, // Faster typing speed
    0
  );

  const { displayedText: asciiText, isTyping: asciiTyping } = useTyping(
    asciiArt,
    15, // Faster typing speed
    1000 // Reduced delay
  );

  const { displayedText: welcomeText, isTyping: welcomeTyping } = useTyping(
    welcomeMessage,
    20, // Faster typing speed
    2000 // Reduced delay
  );

  useEffect(() => {
    if (currentStep < bootSteps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 800); // Reduced from 1500ms to 800ms
      return () => clearTimeout(timer);
    }
  }, [currentStep, bootSteps.length]);

  useEffect(() => {
    if (!stepTyping && !asciiTyping && !welcomeTyping && currentStep >= bootSteps.length - 1) {
      const timer = setTimeout(() => {
        setBootComplete(true);
        onComplete();
      }, 500); // Reduced from 1000ms to 500ms
      return () => clearTimeout(timer);
    }
  }, [stepTyping, asciiTyping, welcomeTyping, currentStep, bootSteps.length, onComplete]);

  if (bootComplete) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-deep-black text-matrix-green font-fira-code z-50 flex items-center justify-center">
      <div className="max-w-4xl mx-auto p-8">
        {/* Boot Steps */}
        <div className="mb-8 space-y-2">
          {bootSteps.slice(0, currentStep + 1).map((step, index) => (
            <div key={index} className="text-lg">
              {index === currentStep ? stepText : step}
            </div>
          ))}
        </div>

        {/* ASCII Art */}
        <div className="mb-8 text-center">
          <pre className="text-matrix-green text-sm md:text-base lg:text-lg whitespace-pre">
            {asciiText}
          </pre>
        </div>

        {/* Welcome Message */}
        <div className="text-center">
          <pre className="text-matrix-green text-sm md:text-base whitespace-pre-line">
            {welcomeText}
          </pre>
        </div>

        {/* Loading Indicator */}
        <div className="mt-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-matrix-green"></div>
        </div>

        {/* Skip Button */}
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setBootComplete(true);
              onComplete();
            }}
            className="px-4 py-2 bg-matrix-green/20 text-matrix-green border border-matrix-green/50 rounded hover:bg-matrix-green/30 transition-colors"
          >
            Skip Boot Sequence
          </button>
        </div>
      </div>
    </div>
  );
};

export default BootSequence;
